import { useEffect, useRef, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { useLang } from '../i18n.js';
import { Icon } from '../shell.js';
import { navigate } from '../router.js';
import { Callout, Dialog, Loading, toast } from '../ui.js';
import { uploadFile } from './ProjectFiles.js';

/**
 * "Send videos for review" (owner 2026-11-04): ONE dialog for one file or many, from the project page, My work, the
 * review page and a video's own menu (then with that video picked). Files are uploaded (or dropped) into the project's
 * Internal / EXPORTS / VIDEO, or ticked there; each file is matched to its video by name, a file that matches none becomes
 * a new video, and Send makes a version per video in the Hub's review. Review in the Hub off, a Ziflow project or no
 * Drive: a video's button moves the status as before (`fallback`).
 */

export interface ReviewFiles {
  on: boolean;
  reason?: string;
  video?: { id: string; name: string; phase: string; step: string; needsFinalFormats: boolean } | null;
  videos?: { id: string; name: string }[];
  folder?: { root: 'internal'; id: string; path: string[] };
  files?: { id: string; name: string; size: number | null; modifiedTime: string | null }[];
  suggested?: string | null;
  /** a Ziflow project: sending moves it to the Hub (owner 2026-11-02), with the videos still in a Ziflow review */
  moveToHub?: { waiting: string[] } | null;
}

const MOVE = {
  en: {
    title: 'The project moves to the Hub',
    body: 'This project is reviewed in Ziflow. When you send, it moves to the Hub: Monday is set to Hub, Ziflow is no longer used for it, and the client gets a new link from the Hub. The round number carries on from Ziflow.',
    waiting: (names: string) => `Still in a Ziflow review: ${names}. Send them from the Hub too.`,
    send: 'Move to the Hub and send'
  },
  sv: {
    title: 'Projektet flyttas till Hubben',
    body: 'Projektet granskas i Ziflow. När du skickar flyttas det till Hubben: Monday sätts till Hub, Ziflow används inte längre för projektet och kunden får en ny länk från Hubben. Rundan räknas vidare från Ziflow.',
    waiting: (names: string) => `Ligger fortfarande i granskning i Ziflow: ${names}. Skicka även dem från Hubben.`,
    send: 'Flytta till Hubben och skicka'
  }
} as const;

/** What the sender confirms before a Ziflow project's first send from the Hub. */
export function MoveToHubNote(p: { move: { waiting: string[] }; skip?: string }) {
  const t = MOVE[useLang()];
  const waiting = p.move.waiting.filter((n) => n !== p.skip);
  return (
    <Callout tone="warn" title={t.title}>
      <p class="small">{t.body}</p>
      {waiting.length ? <p class="small">{t.waiting(waiting.join(', '))}</p> : null}
    </Callout>
  );
}

export const moveToHubLabel = (lang: 'en' | 'sv') => MOVE[lang].send;

const TX = {
  en: {
    title: 'Send videos for review',
    titleOne: (v: string) => `Send for review – ${v}`,
    draft: 'Draft',
    final: 'Final formats',
    drop: 'Drag video files here',
    dropSub: 'one or more – they go to',
    choose: 'Choose files…',
    inFolder: 'Already in the folder',
    more: (n: number) => `Show ${n} more`,
    uploading: (pct: number) => `uploading ${pct}%`,
    uploaded: 'uploaded',
    failed: 'failed',
    reading: 'Matching the files…',
    draftInfo: 'Each file becomes the next version of its video. One file per video. A file that matches no video becomes a new video.',
    finalInfo: 'Each video gets one version with all its final formats. The producer decides first, then the client.',
    pickVideo: 'Pick a video…',
    newVideo: (n: string) => `New video: ${n}`,
    videoFor: (n: string) => `Video for ${n}`,
    formatOf: (n: string) => `Format of ${n}`,
    remove: (n: string) => `Remove ${n}`,
    matched: 'Matched by name',
    isNew: 'New video',
    picked: 'Picked by you',
    pick: 'Pick the video',
    onePerVideo: 'One file per video for drafts.',
    send: (n: number) => (n === 1 ? 'Send 1 video' : `Send ${n} videos`),
    sendNone: 'Send for review',
    sending: 'Sending…',
    cancel: 'Cancel',
    done: 'Done',
    sent: (n: number) => (n === 1 ? '1 video sent for review' : `${n} videos sent for review`),
    sentOne: (v: string) => `${v} is sent for review`,
    version: 'new version',
    notSent: 'Not sent: ',
    open: 'Open the review',
    off: 'Send for review is not available here'
  },
  sv: {
    title: 'Skicka videor för granskning',
    titleOne: (v: string) => `Skicka för granskning – ${v}`,
    draft: 'Utkast',
    final: 'Slutformat',
    drop: 'Dra in videofiler här',
    dropSub: 'en eller flera – de hamnar i',
    choose: 'Välj filer…',
    inFolder: 'Ligger redan i mappen',
    more: (n: number) => `Visa ${n} till`,
    uploading: (pct: number) => `laddar upp ${pct} %`,
    uploaded: 'uppladdad',
    failed: 'misslyckades',
    reading: 'Matchar filerna…',
    draftInfo: 'Varje fil blir nästa version av sin video. En fil per video. En fil som inte matchar någon video blir en ny video.',
    finalInfo: 'Varje video får en version med alla sina slutformat. Producenten beslutar först, sedan kunden.',
    pickVideo: 'Välj en video…',
    newVideo: (n: string) => `Ny video: ${n}`,
    videoFor: (n: string) => `Video för ${n}`,
    formatOf: (n: string) => `Format för ${n}`,
    remove: (n: string) => `Ta bort ${n}`,
    matched: 'Matchad på namn',
    isNew: 'Ny video',
    picked: 'Vald av dig',
    pick: 'Välj video',
    onePerVideo: 'En fil per video för utkast.',
    send: (n: number) => (n === 1 ? 'Skicka 1 video' : `Skicka ${n} videor`),
    sendNone: 'Skicka för granskning',
    sending: 'Skickar…',
    cancel: 'Avbryt',
    done: 'Klar',
    sent: (n: number) => (n === 1 ? '1 video skickad för granskning' : `${n} videor skickade för granskning`),
    sentOne: (v: string) => `${v} är skickad för granskning`,
    version: 'ny version',
    notSent: 'Inte skickad: ',
    open: 'Öppna granskningen',
    off: 'Skicka för granskning går inte här'
  }
} as const;

const NEW = 'new';
const SHOWN = 6;
type Kind = 'draft' | 'final';
type Up = { key: string; name: string; size: number; sent: number; state: 'uploading' | 'done' | 'failed'; error?: string };
interface Row {
  id: string;
  name: string;
  videoId: string | null;
  newName: string;
  format: string | null;
  problem: string | null;
  /** what the Hub matched from the name (to say "Matched by name" or "Picked by you") */
  guess: string | null;
}
interface Result {
  videoId: string | null;
  videoName: string;
  ok: boolean;
  newVersion: boolean;
  error: string | null;
  versionId?: string | null;
}

const mb = (n: number | null) => (n === null ? '' : n >= 1e9 ? `${(n / 1e9).toFixed(1)} GB` : `${Math.max(1, Math.round(n / 1e6))} MB`);
const day = (iso: string | null, lang: 'en' | 'sv') => (iso ? new Date(iso).toLocaleString(lang === 'sv' ? 'sv-SE' : 'en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Stockholm' }) : '');
/** The video's own kind: an approved draft that needs final formats, or a video already in final formats, sends finals. */
const kindFor = (v: ReviewFiles['video']): Kind => (v && (v.phase === 'final' || (v.phase === 'draft' && v.step === 'approved' && v.needsFinalFormats)) ? 'final' : 'draft');

/** The one dialog (owner 2026-11-04). `videoId`: opened from a video, which is then picked for its file. */
export function SendForReview(p: { api: Api; projectId: string; videoId?: string; videoName?: string; kind?: Kind; files?: File[]; fallback?: () => Promise<void>; onDone: () => Promise<void>; onClose: () => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [data, setData] = useState<ReviewFiles | null>(null);
  const [kind, setKind] = useState<Kind>(p.kind ?? 'draft');
  const [picked, setPicked] = useState<string[]>([]);
  const [ups, setUps] = useState<Up[]>([]);
  const [rows, setRows] = useState<Row[]>([]);
  const [matching, setMatching] = useState(false);
  const [all, setAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<Result[] | null>(null);
  const choices = useRef(new Map<string, Partial<Row>>());
  useEffect(() => {
    let live = true;
    const url = p.videoId ? `/api/projects/${p.projectId}/videos/${p.videoId}/review-files` : `/api/projects/${p.projectId}/review-files`;
    p.api
      .get<ReviewFiles>(url)
      .then(async (r) => {
        if (!live) return;
        if (!r.on && p.fallback) {
          // no file to pick here: the video's button does what it always did
          p.onClose();
          await p.fallback();
          return;
        }
        setData(r);
        if (!p.kind) setKind(kindFor(r.video));
        if (r.suggested) setPicked([r.suggested]);
        if (r.on && p.files?.length) void upload(r, p.files);
      })
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [p.projectId, p.videoId]);

  // match the picked files to videos whenever the pick or the kind changes; the person's own choices stay
  useEffect(() => {
    if (!data?.on || !picked.length) {
      setRows([]);
      return;
    }
    let live = true;
    setMatching(true);
    p.api
      .post<{ files: Omit<Row, 'guess'>[] }>(`/api/projects/${p.projectId}/review/preview`, { kind, fileIds: picked })
      .then((r) => {
        if (!live) return;
        const one = picked.length === 1 && p.videoId;
        setRows(
          r.files.map((f) => {
            const guess = one && (f.videoId === NEW || !f.videoId) ? p.videoId! : f.videoId;
            return { ...f, guess, videoId: guess, ...choices.current.get(f.id) };
          })
        );
      })
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => live && setMatching(false));
    return () => {
      live = false;
    };
  }, [data, picked.join(','), kind]);

  const upload = async (d: ReviewFiles, list: File[]) => {
    const files = list.filter((f) => f.size > 0);
    if (!d.folder || !files.length) return;
    setError(null);
    const state: Up[] = files.map((f, i) => ({ key: `${Date.now()}-${i}`, name: f.name, size: f.size, sent: 0, state: 'uploading' }));
    const show = () => setUps((now) => [...now.filter((u) => !state.some((x) => x.key === u.key)), ...state.map((x) => ({ ...x }))]);
    show();
    // one at a time, like the Files tab: each file goes through the Hub to Drive in chunks
    for (const [i, f] of files.entries()) {
      try {
        const made = await uploadFile(p.api, p.projectId, { root: 'internal', folder: d.folder.id }, f, (sent) => {
          state[i]!.sent = sent;
          show();
        });
        state[i]!.state = 'done';
        setData((now) => (now ? { ...now, files: [{ id: made.id, name: made.name, size: f.size, modifiedTime: new Date().toISOString() }, ...(now.files ?? [])] } : now));
        setPicked((now) => (now.includes(made.id) ? now : [...now, made.id]));
      } catch (e) {
        state[i]!.state = 'failed';
        state[i]!.error = e instanceof Error ? e.message : String(e);
      }
      show();
    }
  };

  const toggle = (id: string) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
  const set = (id: string, patch: Partial<Row>) => {
    choices.current.set(id, { ...choices.current.get(id), ...patch });
    setRows(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const draft = kind === 'draft';
  const key = (r: Row) => (r.videoId === NEW ? `new:${r.newName.toLowerCase()}` : r.videoId);
  const counts = new Map<string, number>();
  for (const r of rows) {
    const k = key(r);
    if (k) counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const twice = draft && [...counts.values()].some((n) => n > 1);
  const uploading = ups.some((u) => u.state === 'uploading');
  const blocked = !rows.length || matching || uploading || rows.some((r) => !r.videoId || r.problem) || twice;
  const nVideos = counts.size;

  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await p.api.post<{ results: Result[] }>(`/api/projects/${p.projectId}/review/submit`, {
        kind,
        files: rows.map((x) => ({ fileId: x.id, videoId: x.videoId, ...(draft ? {} : { format: x.format }) })),
        ...(data?.moveToHub ? { moveToHub: true } : {})
      });
      await p.onDone();
      const ok = r.results.filter((x) => x.ok);
      if (ok.length === 1 && r.results.length === 1) {
        // one video: close and say so, the toast's button opens the new version
        const res = ok[0]!;
        p.onClose();
        const url = `/projects/${p.projectId}/review/${res.videoId}${res.versionId ? `?version=${res.versionId}` : ''}`;
        toast({ text: t.sentOne(res.videoName), undoLabel: t.open, undo: () => navigate(url) });
        return;
      }
      setResults(r.results);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data && !error) return null;
  const folder = data?.folder ? data.folder.path.join(' / ') : '';
  const listed = (data?.files ?? []).filter((f) => !ups.some((u) => u.state === 'done' && u.name === f.name) || picked.includes(f.id));
  const shown = all ? listed : listed.slice(0, SHOWN);
  const title = p.videoName ? t.titleOne(p.videoName) : t.title;
  const okCount = results?.filter((x) => x.ok).length ?? 0;
  const open = (r: Result) => `/projects/${p.projectId}/review/${r.videoId}${r.versionId ? `?version=${r.versionId}` : ''}`;
  return (
    <Dialog
      title={results ? t.sent(okCount) : title}
      onClose={p.onClose}
      wide
      foot={
        results ? (
          <button type="button" class="btn btn-primary" onClick={p.onClose}>
            {t.done}
          </button>
        ) : (
          <>
            <button type="button" class="btn" onClick={p.onClose}>
              {t.cancel}
            </button>
            <button type="button" class="btn btn-primary" disabled={!data?.on || blocked || busy} onClick={() => void send()}>
              {busy ? t.sending : data?.moveToHub ? moveToHubLabel(lang) : nVideos ? t.send(nVideos) : t.sendNone}
            </button>
          </>
        )
      }
    >
      {results ? (
        <ul class="sv-res">
          {results.map((r) => (
            <li>
              <strong>{r.videoName}</strong>{' '}
              {r.ok ? (
                <span class="small">
                  {t.version} ·{' '}
                  <a href={open(r)} onClick={(e) => (e.preventDefault(), p.onClose(), navigate(open(r)))}>
                    {t.open}
                  </a>
                </span>
              ) : (
                <span class="small field-error" role="alert">
                  {t.notSent}
                  {r.error}
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : !data ? (
        <p class="field-error small">{error}</p>
      ) : !data.on ? (
        <Callout tone="warn" title={t.off}>
          <p class="small">{data.reason}</p>
        </Callout>
      ) : (
        <div
          class={`sv${over ? ' is-over' : ''}`}
          onDragOver={(e) => (e.preventDefault(), setOver(true))}
          onDragLeave={() => setOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setOver(false);
            void upload(data, [...(e.dataTransfer?.files ?? [])]);
          }}
        >
          {data.moveToHub ? <MoveToHubNote move={data.moveToHub} skip={p.videoName} /> : null}
          <div class="seg" role="radiogroup" aria-label={t.title}>
            {(['draft', 'final'] as const).map((k) => (
              <button type="button" role="radio" aria-checked={kind === k} class={`seg-btn${kind === k ? ' is-on' : ''}`} onClick={() => setKind(k)}>
                {k === 'draft' ? t.draft : t.final}
              </button>
            ))}
          </div>
          <label class="sv-drop">
            <Icon name="upload" />
            <strong>{t.drop}</strong>
            <span class="small muted">
              {t.dropSub} {folder}
            </span>
            <span class="btn btn-sm">{t.choose}</span>
            <input
              type="file"
              multiple
              class="sr-only"
              onChange={(e) => {
                const input = e.currentTarget as HTMLInputElement;
                const files = [...(input.files ?? [])];
                input.value = '';
                void upload(data, files);
              }}
            />
          </label>
          {ups.length ? (
            <ul class="sv-ups" aria-live="polite">
              {ups.map((u) => (
                <li>
                  <span class="sv-up-name">{u.name}</span>
                  <span class={`small ${u.state === 'failed' ? 'field-error' : 'muted'}`}>{u.state === 'failed' ? `${t.failed}: ${u.error ?? ''}` : u.state === 'done' ? t.uploaded : t.uploading(Math.floor((u.sent / u.size) * 100))}</span>
                  {u.state === 'uploading' ? <progress max={u.size} value={u.sent} aria-label={u.name} /> : null}
                </li>
              ))}
            </ul>
          ) : null}
          {listed.length ? (
            <details class="sv-folder" open={!ups.length && !p.files?.length}>
              <summary>
                {t.inFolder} <span class="fold-n">{listed.length}</span>
              </summary>
              <ul class="sv-files">
                {shown.map((f) => (
                  <li>
                    <label>
                      <input type="checkbox" checked={picked.includes(f.id)} onChange={() => toggle(f.id)} />
                      <span class="sv-file">
                        <span class="sv-file-name">{f.name}</span>
                        <span class="small muted">{[day(f.modifiedTime, lang), mb(f.size)].filter(Boolean).join(' · ')}</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
              {!all && listed.length > SHOWN ? (
                <button type="button" class="btn btn-sm btn-ghost" onClick={() => setAll(true)}>
                  {t.more(listed.length - SHOWN)}
                </button>
              ) : null}
            </details>
          ) : null}
          {rows.length ? (
            <>
              <p class="small muted">{draft ? t.draftInfo : t.finalInfo}</p>
              <ul class="sv-rows">
                {rows.map((r) => (
                  <li class="sv-row">
                    <span class="sv-row-name">
                      <span title={r.name}>{r.name}</span>
                      {r.problem ? <span class="small field-error">{r.problem}</span> : null}
                    </span>
                    <select class="input" aria-label={t.videoFor(r.name)} value={r.videoId ?? ''} onChange={(e) => set(r.id, { videoId: (e.target as HTMLSelectElement).value || null })}>
                      <option value="">{t.pickVideo}</option>
                      {(data.videos ?? []).map((v) => (
                        <option value={v.id}>{v.name}</option>
                      ))}
                      <option value={NEW}>{t.newVideo(r.newName)}</option>
                    </select>
                    {draft ? null : <input class="input sv-fmt" aria-label={t.formatOf(r.name)} value={r.format ?? ''} maxLength={20} onInput={(e) => set(r.id, { format: (e.target as HTMLInputElement).value })} />}
                    <span class={`pill ${!r.videoId ? 'pill--crit' : r.videoId === NEW ? 'pill--info' : r.videoId === r.guess ? 'pill--ok' : 'pill--neutral'}`}>{!r.videoId ? t.pick : r.videoId === NEW ? t.isNew : r.videoId === r.guess ? t.matched : t.picked}</span>
                    <button type="button" class="btn btn-sm btn-ghost" aria-label={t.remove(r.name)} onClick={() => toggle(r.id)}>
                      <Icon name="x" />
                    </button>
                  </li>
                ))}
              </ul>
              {twice ? <p class="field-error small">{t.onePerVideo}</p> : null}
            </>
          ) : matching ? (
            <Loading label={t.reading} />
          ) : null}
          {error ? <p class="field-error small">{error}</p> : null}
        </div>
      )}
    </Dialog>
  );
}
